import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";

export default function VerifyPage() {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, valid: false, cert: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/verify/${id}`);
        setState({ loading: false, valid: true, cert: data.certificate });
      } catch (e) {
        setState({ loading: false, valid: false, cert: null });
      }
    })();
  }, [id]);

  if (state.loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Arial, sans-serif",
          color: "#5f6368",
        }}
      >
        Loading…
      </div>
    );
  }

  if (!state.valid) {
    return (
      <PlainPage>
        <h1 style={{ fontSize: "22px", color: "#202124", margin: "0 0 12px" }}>
          Certificate not found
        </h1>
        <p style={{ color: "#5f6368", lineHeight: 1.5 }}>
          No record matches reference{" "}
          <code
            style={{
              background: "#f1f3f4",
              padding: "1px 6px",
              borderRadius: "3px",
              fontFamily: "monospace",
            }}
          >
            {id}
          </code>
          . The link may be invalid or the record removed.
        </p>
      </PlainPage>
    );
  }

  const c = state.cert;

  return (
    <PlainPage>
      <h1 style={{ fontSize: "22px", color: "#1a73e8", margin: "0 0 6px", fontWeight: 400 }}>
        Certificate Verification
      </h1>
      <div style={{ fontSize: "13px", color: "#5f6368", marginBottom: "20px" }}>
        Reference {c.id}
      </div>

      <div
        style={{
          background: "#e6f4ea",
          border: "1px solid #ceead6",
          color: "#137333",
          padding: "10px 14px",
          borderRadius: "4px",
          fontSize: "14px",
          marginBottom: "24px",
        }}
      >
        ✓ This record is on file.
      </div>

      <Section>
        <Item label="Holder Name" value={c.holder_name} />
        <Item label="COC Number" value={c.coc_number} />
        <Item label="Rank" value={c.rank} />
        <Item label="Flag State" value={c.flag_state} />
        <Item label="Issued By" value={c.issued_by_country} />
        <Item label="Holding Certificate №" value={c.holding_certificate_no} />
        <Item label="Regulation" value={c.regulation} />
        <Item label="Cert. of Equivalent №" value={c.cert_equivalent_no} />
        <Item label="Issued On" value={c.issued_on} />
        <Item label="Place of Issue" value={c.place_of_issue} />
        <Item label="COC Issue Date" value={c.coc_issue_date || "—"} />
        <Item label="Valid Until" value={c.valid_until} />
        <Item label="Date of Birth" value={c.date_of_birth} />
        <Item label="Nationality" value={c.nationality} />
      </Section>
    </PlainPage>
  );
}

/* ---------- helpers ---------- */
function PlainPage({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#ffffff",
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#202124",
        padding: "32px 16px",
      }}
      data-testid="verify-page"
    >
      <div style={{ maxWidth: "780px", margin: "0 auto" }}>{children}</div>
    </div>
  );
}

function Section({ children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "20px 32px",
        alignItems: "start",
      }}
    >
      {children}
    </div>
  );
}

function Item({ label, value }) {
  return (
    <div>
      <div
        style={{
          fontSize: "12px",
          color: "#202124",
          fontWeight: 700,
          marginBottom: "4px",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "15px",
          color: "#000000",
          fontWeight: 700,
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Th({ children }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "8px 10px",
        borderBottom: "2px solid #dadce0",
        fontWeight: 500,
        color: "#5f6368",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td
      style={{
        padding: "8px 10px",
        borderBottom: "1px solid #f1f3f4",
      }}
    >
      {children}
    </td>
  );
}
